'use strict';

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * IntegerInput - A controlled input component that only accepts integer values
 *
 * Features:
 * - Only allows digits (0-9)
 * - Optional min/max validation
 * - HTML5 validation with pattern
 * - Works with both metric and imperial units
 * - Automatically sanitizes decimal values from unit conversions
 */
const IntegerInput = ({
    value,
    onChange,
    onError,
    min,
    max,
    placeholder,
    disabled,
    autoFocus,
    className,
    name,
    id,
    tabIndex,
    'data-testid': dataTestId
}) => {
    const [error, setError] = useState(null);

    const validationMessage = `Must be a whole number${min !== undefined ? ` (min: ${min})` : ''}${max !== undefined ? ` (max: ${max})` : ''}`;

    // Sanitize value BEFORE render to prevent visual flash
    const sanitizedValue = useMemo(() => {
        if (value === '' || value === null || value === undefined) {
            return '';
        }

        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        // If value has decimals, round it to integer
        if (!isNaN(numValue) && numValue !== parseInt(numValue, 10)) {
            return Math.round(numValue);
        }

        return value;
    }, [value]);

    const handleChange = (e) => {
        let inputValue = e.target.value;

        // Allow empty value
        if (inputValue === '') {
            setError(null);
            onChange && onChange('');
            return;
        }

        // Remove any non-digit characters (including decimal points)
        inputValue = inputValue.replace(/[^\d]/g, '');

        // If nothing left after sanitization, treat as empty
        if (inputValue === '') {
            setError(null);
            onChange && onChange('');
            return;
        }

        // Parse as integer
        const intValue = parseInt(inputValue, 10);

        // Validate min/max
        if (min !== undefined && intValue < min) {
            const errorMsg = `Value must be at least ${min}`;
            setError(errorMsg);
            onError && onError(errorMsg);
            return;
        }

        if (max !== undefined && intValue > max) {
            const errorMsg = `Value must be at most ${max}`;
            setError(errorMsg);
            onError && onError(errorMsg);
            return;
        }

        // Valid integer
        setError(null);
        onError && onError(null);
        onChange && onChange(intValue);
    };

    const handleKeyPress = (e) => {
        // Only allow digits, backspace, delete, arrow keys, tab
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

        if (allowedKeys.includes(e.key)) {
            return;
        }

        // Block non-digit characters (including decimal point and comma)
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e) => {
        // Get pasted data
        const pastedData = e.clipboardData.getData('text');

        // Check if it contains only digits
        if (!/^\d+$/.test(pastedData)) {
            e.preventDefault();
        }
    };

    return (
        <input
            type="number"
            id={id}
            name={name}
            value={sanitizedValue}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            tabIndex={tabIndex}
            className={className}
            pattern="[0-9]*"
            inputMode="numeric"
            data-testid={dataTestId}
        />
    );
};

IntegerInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
    onError: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.string,
    tabIndex: PropTypes.number,
    'data-testid': PropTypes.string,
};

IntegerInput.defaultProps = {
    value: '',
    disabled: false,
    autoFocus: false,
};

export default IntegerInput;
